/**
 * Web3Auth No-Modal integration for social logins.
 * Uses Web3Auth for Google, X/Twitter, Apple, Discord, Facebook, Reddit,
 * GitHub, Twitch, WeChat, Line, Kakao, and Farcaster.
 *
 * EVM wallets (MetaMask, Rabby, etc.) are handled separately via EIP-6963.
 */
import { Web3AuthNoModal } from "@web3auth/no-modal"
import { AuthAdapter } from "@web3auth/auth-adapter"
import { EthereumPrivateKeyProvider } from "@web3auth/ethereum-provider"
import { CHAIN_NAMESPACES, WEB3AUTH_NETWORK } from "@web3auth/base"

const CLIENT_ID = process.env.REACT_APP_WEB3AUTH_CLIENT_ID || "BJ_6Qz2rJK6s0o6i9eFkB9ukcjO6IhkV4ony855aqXMipLmOlqeEzU4fEWh_xuMlEY-G69YOXy8KuDK5YDjbDS0"

const NIBIRU_CHAIN_CONFIG = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: process.env.REACT_APP_NIBIRU_EVM_CHAIN_ID || "0x1AFF",
  rpcTarget: process.env.REACT_APP_NIBIRU_EVM_RPC || "https://evm-rpc.nibiru.fi",
  displayName: "Nibiru EVM",
  blockExplorerUrl: process.env.REACT_APP_NIBIRU_EVM_EXPLORER || "https://nibiscan.io",
  ticker: "NIBI",
  tickerName: "Nibiru",
}

let web3authInstance = null
let initPromise = null

/**
 * Initialize Web3Auth singleton (lazy, called on first use).
 */
export async function getWeb3Auth() {
  if (web3authInstance?.connected || web3authInstance?.status === "ready") {
    return web3authInstance
  }

  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      const privateKeyProvider = new EthereumPrivateKeyProvider({
        config: { chainConfig: NIBIRU_CHAIN_CONFIG },
      })

      const web3auth = new Web3AuthNoModal({
        clientId: CLIENT_ID,
        web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
        privateKeyProvider,
      })

      const authAdapter = new AuthAdapter({
        adapterSettings: {
          uxMode: "popup",
          loginConfig: {},
        },
        privateKeyProvider,
      })

      web3auth.configureAdapter(authAdapter)
      await web3auth.init()

      web3authInstance = web3auth
      return web3auth
    } catch (error) {
      console.error("Web3Auth init error:", error)
      initPromise = null
      throw error
    }
  })()

  return initPromise
}

/**
 * Connect via Web3Auth social login.
 * @param {string} loginProvider - e.g. "google", "twitter", "apple", "discord", "facebook", etc.
 * @returns {Promise<{address: string, provider: object}>}
 */
export async function connectSocial(loginProvider) {
  const web3auth = await getWeb3Auth()

  // If already connected, disconnect first
  if (web3auth.connected) {
    try { await web3auth.logout() } catch {}
  }

  let web3authProvider
  try {
    web3authProvider = await web3auth.connectTo("auth", {
      loginProvider,
    })
  } catch (err) {
    // Normalize Web3Auth errors into simple messages
    const msg = (err?.message || String(err)).toLowerCase()
    if (
      msg.includes("user closed") ||
      msg.includes("popup closed") ||
      msg.includes("user rejected") ||
      msg.includes("user cancelled") ||
      msg.includes("failed to login with") ||
      msg.includes("failed to connect")
    ) {
      throw new Error("USER_CANCELLED")
    }
    throw new Error(err?.message || "Connection failed")
  }

  if (!web3authProvider) {
    throw new Error("Web3Auth connection returned no provider")
  }

  // Store as the active EIP-1193 provider so signWithEvm / other code can find it
  const { setSelectedProvider } = await import("./eip6963")
  setSelectedProvider(web3authProvider)

  // Get address from the provider
  const { ethers } = await import("ethers")
  const provider = new ethers.BrowserProvider(web3authProvider)
  const signer = await provider.getSigner()
  const address = await signer.getAddress()

  return { address, provider: web3authProvider, ethersProvider: provider }
}

/**
 * Disconnect Web3Auth session.
 */
export async function disconnectWeb3Auth() {
  if (web3authInstance?.connected) {
    try { await web3authInstance.logout() } catch {}
  }
}

/**
 * Check if Web3Auth has an active session.
 */
export function isWeb3AuthConnected() {
  return web3authInstance?.connected || false
}
